import React, { useState, useEffect } from 'react';
import './PermissionsScreen.css';

const PermissionsScreen = ({ isOpen, onClose, onComplete, userInfo }) => {
  const [cameraPermission, setCameraPermission] = useState('prompt'); // 'prompt', 'granted', 'denied'
  const [micPermission, setMicPermission] = useState('prompt'); // 'prompt', 'granted', 'denied'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Check current permissions when component opens
  useEffect(() => {
    if (isOpen) {
      checkPermissions();
    }
  }, [isOpen]);

  const checkPermissions = async () => {
    try {
      // Check camera permission
      const cameraStream = await navigator.mediaDevices.getUserMedia({ video: true });
      setCameraPermission('granted');
      // Stop the stream immediately
      cameraStream.getTracks().forEach(track => track.stop());
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setCameraPermission('denied');
      } else {
        setCameraPermission('prompt');
      }
    }

    try {
      // Check microphone permission
      const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicPermission('granted');
      // Stop the stream immediately
      micStream.getTracks().forEach(track => track.stop());
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setMicPermission('denied');
      } else {
        setMicPermission('prompt');
      }
    }
  };

  const requestCameraPermission = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setCameraPermission('granted');
      // Stop the stream immediately
      stream.getTracks().forEach(track => track.stop());
    } catch (err) {
      console.error('Camera permission error:', err);
      if (err.name === 'NotAllowedError') {
        setCameraPermission('denied');
        setError('Camera permission was denied. Please enable it in your browser settings.');
      } else {
        setError('Failed to access camera. Please check your device settings.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const requestMicPermission = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicPermission('granted');
      // Stop the stream immediately
      stream.getTracks().forEach(track => track.stop());
    } catch (err) {
      console.error('Microphone permission error:', err);
      if (err.name === 'NotAllowedError') {
        setMicPermission('denied');
        setError('Microphone permission was denied. Please enable it in your browser settings.');
      } else {
        setError('Failed to access microphone. Please check your device settings.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = () => {
    // Store permission status in localStorage
    localStorage.setItem('camera_permission', cameraPermission);
    localStorage.setItem('mic_permission', micPermission);
    
    // Complete the permissions setup
    const updatedUserInfo = {
      ...userInfo,
      cameraPermission,
      micPermission
    };
    
    onComplete(updatedUserInfo);
  };

  const handleBack = () => {
    if (onClose) {
      onClose();
    }
  };

  const canContinue = cameraPermission === 'granted' && micPermission === 'granted';

  if (!isOpen) return null;

  return (
    <div className="permissions-overlay">
      <div className="permissions-container">
        {/* Back Button */}
        <button className="back-button" onClick={handleBack}>
          ← Back
        </button>
        
        {isLoading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>Requesting permissions...</p>
          </div>
        )}
        
        {/* Background decorative elements */}
        <div className="background-decorations">
          <div className="decoration-circle circle-1"></div>
          <div className="decoration-circle circle-2"></div>
          <div className="decoration-circle circle-3"></div>
          <div className="decoration-line line-1"></div>
          <div className="decoration-line line-2"></div>
        </div>

        {/* Main content */}
        <div className="main-content">
          <div className="content-wrapper">
            <h2 className="main-title">
              Enable Camera & Microphone<br />
              <span className="subtitle">For the best chat experience</span>
            </h2>
            
            <div className="permissions-container">
              {/* Camera Permission */}
              <div className="permission-item">
                <div className="permission-icon">
                  <span className="icon">📹</span>
                </div>
                <div className="permission-content">
                  <h3 className="permission-title">Camera Access</h3>
                  <p className="permission-description">
                    Allow camera access to see and be seen during video chats
                  </p>
                  <div className="permission-status">
                    {cameraPermission === 'granted' && (
                      <span className="status granted">✓ Granted</span>
                    )}
                    {cameraPermission === 'denied' && (
                      <span className="status denied">✗ Denied</span>
                    )}
                    {cameraPermission === 'prompt' && (
                      <span className="status pending">⏳ Pending</span>
                    )}
                  </div>
                  {cameraPermission !== 'granted' && (
                    <button 
                      className="permission-button"
                      onClick={requestCameraPermission}
                      disabled={isLoading}
                    >
                      Enable Camera
                    </button>
                  )}
                </div>
              </div>

              {/* Microphone Permission */}
              <div className="permission-item">
                <div className="permission-icon">
                  <span className="icon">🎤</span>
                </div>
                <div className="permission-content">
                  <h3 className="permission-title">Microphone Access</h3>
                  <p className="permission-description">
                    Allow microphone access to hear and be heard during video chats
                  </p>
                  <div className="permission-status">
                    {micPermission === 'granted' && (
                      <span className="status granted">✓ Granted</span>
                    )}
                    {micPermission === 'denied' && (
                      <span className="status denied">✗ Denied</span>
                    )}
                    {micPermission === 'prompt' && (
                      <span className="status pending">⏳ Pending</span>
                    )}
                  </div>
                  {micPermission !== 'granted' && (
                    <button 
                      className="permission-button"
                      onClick={requestMicPermission}
                      disabled={isLoading}
                    >
                      Enable Microphone
                    </button>
                  )}
                </div>
              </div>

              {error && <div className="error-message">{error}</div>}
              
              <button 
                className="next-button"
                disabled={!canContinue || isLoading}
                onClick={handleContinue}
              >
                <span>Continue</span>
                <div className="button-shine"></div>
              </button>
            </div>
          </div>
        </div>

        {/* Character avatars */}
        <div className="character-avatars">
          <div className="avatar-left">
            <img src={'/images/icons/character_1.svg'} alt="Character 1" />
          </div>
          <div className="avatar-right">
            <img src={'/images/icons/character_2.svg'} alt="Character 2" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionsScreen; 